import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { paymentApi } from 'api'
import {
  selectPayPalPlanId,
  selectSubscriptionCurrentPrice,
  selectSubscriptionPeriodName,
  selectSubscriptionPeriodQuantity,
  selectSubscriptionTrialPeriodPrice,
  selectSubscriptionTrialPeriodDays,
  selectCurrency,
} from 'modules/purchase/redux/selects'
import { selectScreenName } from 'root-redux/selects/common'
import { selectUUID } from 'root-redux/selects/user'
import lottie from 'lottie-web/build/player/lottie_light'
import paymentSuccessAnimation from 'assets/animations/paymentSuccessAnimation.json'
import paymentErrorAnimation from 'assets/animations/paymentErrorAnimation.json'
import { PaymentMethod } from 'modules/purchase/constants'

import { logSuccessfulPayPalPayment } from 'modules/purchase/helpers/logSuccessfulPayPalPayment'
import { logStartedPayPalPayment } from 'modules/purchase/helpers/logStartedPayPalPayment'
import { createProductId } from 'helpers/createProductId'
import { eventLogger } from 'services/eventLogger.service'
import { logFailedPayPalPayment } from '../helpers/logFailedPayPalPayment'

export const enum AnimationState {
  SUCCESS = 'success',
  ERROR = 'error',
}

type TValues = {
  isButtonTouched: boolean
  isPaymentStatusShown: boolean
  setIsPaymentStatusShown: (value: boolean) => void
  animationStatus: AnimationState | boolean
  completeAnimationRef: React.MutableRefObject<HTMLDivElement | null>
  errorAnimationRef: React.MutableRefObject<HTMLDivElement | null>
  handleResetError: () => void
  handlePaymentApprove: (data) => Promise<void>
  handlePaymentError: (error) => void
  handlePaymentCancel: () => void
  handleButtonClick: () => void
}

export const usePayPalButton = (): TValues => {
  const [isPaymentStatusShown, setIsPaymentStatusShown] = useState(false)
  const [isButtonTouched, setIsButtonTouched] = useState(false)
  const [animationStatus, setAnimationStatus] = useState<
    AnimationState | boolean
  >(false)

  const currentPrice = useSelector(selectSubscriptionCurrentPrice)
  const currentUUID = useSelector(selectUUID)
  const currentPeriodName = useSelector(selectSubscriptionPeriodName)
  const currentPeriodQuantity = useSelector(selectSubscriptionPeriodQuantity)
  const paypalPlanId = useSelector(selectPayPalPlanId)
  const currency = useSelector(selectCurrency)
  const trialPrice = useSelector(selectSubscriptionTrialPeriodPrice)
  const trialPeriodDays = useSelector(selectSubscriptionTrialPeriodDays)
  const screenName = useSelector(selectScreenName)

  const productId = createProductId({
    periodName: currentPeriodName,
    periodQuantity: currentPeriodQuantity,
    price: trialPrice || currentPrice,
  })
  const completeAnimationRef = useRef<HTMLDivElement | null>(null)
  const errorAnimationRef = useRef<HTMLDivElement | null>(null)

  const handleResetError = useCallback(() => {
    setIsPaymentStatusShown(false)
    setAnimationStatus(false)
  }, [])

  const handlePaymentApprove = useCallback(
    async (data) => {
      setIsButtonTouched(false)
      logSuccessfulPayPalPayment({
        productId: paypalPlanId,
        price: currentPrice,
        subscriptionId: data.subscriptionID,
        uuid: currentUUID,
        periodName: currentPeriodName,
        periodQuantity: currentPeriodQuantity,
        paymentMethod: PaymentMethod.PAYPAL,
        currency,
        trialPrice,
        trialPeriodDays,
        screenName,
        isTrialActive: !!trialPeriodDays,
      })
      const response = await paymentApi.createPaypalSubscription({
        uuid: currentUUID,
        paypalPlanId,
      })
      if (response.status) {
        setIsPaymentStatusShown(true)
        setAnimationStatus(AnimationState.SUCCESS)
      }
    },
    [
      currency,
      currentPrice,
      currentUUID,
      currentPeriodName,
      currentPeriodQuantity,
      paypalPlanId,
      trialPeriodDays,
      trialPrice,
      screenName,
    ],
  )

  const handlePaymentError = useCallback(
    (error) => {
      setIsButtonTouched(false)
      logFailedPayPalPayment({
        productId,
        price: currentPrice,
        error,
        paymentMethod: PaymentMethod.PAYPAL,
        currency,
        screenName,
        isTrialActive: !!trialPeriodDays,
      })
      setIsPaymentStatusShown(true)
      setAnimationStatus(AnimationState.ERROR)
    },

    [currency, currentPrice, productId, screenName, trialPeriodDays],
  )

  const handlePaymentCancel = useCallback(() => {
    setIsButtonTouched(false)
    eventLogger.logPayPalPaymentPopupClose()
  }, [])

  const handleButtonClick = useCallback(() => {
    setIsButtonTouched(true)

    eventLogger.logPaymentMethodSelected(PaymentMethod.PAYPAL)
    logStartedPayPalPayment({
      productId,
      price: currentPrice,
      paymentMethod: PaymentMethod.PAYPAL,
      currency,
      screenName,
      isTrialActive: !!trialPeriodDays,
    })
  }, [currentPrice, currency, productId, screenName, trialPeriodDays])

  useEffect(() => {
    switch (animationStatus) {
      case AnimationState.SUCCESS:
        if (completeAnimationRef.current) {
          lottie.loadAnimation({
            container: completeAnimationRef.current,
            animationData: paymentSuccessAnimation,
            loop: false,
          })
        }
        break
      case AnimationState.ERROR:
        if (errorAnimationRef.current) {
          lottie.loadAnimation({
            container: errorAnimationRef.current,
            animationData: paymentErrorAnimation,
            loop: false,
          })
        }
        break
      default:
        break
    }

    return () => lottie.destroy()
  }, [animationStatus])

  return {
    isButtonTouched,
    isPaymentStatusShown,
    setIsPaymentStatusShown,
    animationStatus,
    completeAnimationRef,
    errorAnimationRef,
    handleResetError,
    handlePaymentApprove,
    handlePaymentError,
    handlePaymentCancel,
    handleButtonClick,
  }
}
